import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  loading: false,
  error: null,
  requisitions: [],
  myRequisitions: [],
  selectedRequisition: null,
  requisitionDetails: {},
  detailsLoading: {},
  detailsError: {},
  actionLoading: {},
};

const requisitionSlice = createSlice({
  name: "companyRequisition",
  initialState,
  reducers: {
    // Get Requisitions
    getRequisitionsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    getRequisitionsSuccess: (state, action) => {
      state.loading = false;
      state.requisitions = Array.isArray(action.payload) ? action.payload : [];
      state.error = null;
    },
    getRequisitionsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Get My Requisitions
    getMyRequisitionsSuccess: (state, action) => {
      state.loading = false;
      state.myRequisitions = Array.isArray(action.payload) ? action.payload : [];
      state.error = null;
    },

    // Create Requisition
    createRequisitionStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    createRequisitionSuccess: (state, action) => {
      state.loading = false;
      state.requisitions.unshift(action.payload);
      state.myRequisitions.unshift(action.payload);
      state.error = null;
    },
    createRequisitionFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update Requisition
    updateRequisitionStart: (state, action) => {
      if (action.payload) {
        state.actionLoading[action.payload] = true;
      } else {
        state.loading = true;
      }
      state.error = null;
    },
    updateRequisitionSuccess: (state, action) => {
      const { id, data } = action.payload;
      if (id) {
        state.actionLoading[id] = false;
      }
      state.loading = false;
      state.error = null;

      if (data) {
        state.requisitions = state.requisitions.map((req) =>
          String(req.id) === String(id) ? { ...req, ...data } : req
        );
        state.myRequisitions = state.myRequisitions.map((req) =>
          String(req.id) === String(id) ? { ...req, ...data } : req
        );
        delete state.requisitionDetails[id];
      }
    },
    updateRequisitionFailure: (state, action) => {
      const { id, error } = action.payload || {};
      if (id) {
        state.actionLoading[id] = false;
      }
      state.loading = false;
      state.error = error || action.payload;
    },

    // Get Requisition Details
    getRequisitionDetailsStart: (state, action) => {
      state.detailsLoading[action.payload] = true;
      state.detailsError[action.payload] = null;
    },
    getRequisitionDetailsSuccess: (state, action) => {
      const { id, data } = action.payload;
      state.detailsLoading[id] = false;
      state.requisitionDetails[id] = data;
      state.detailsError[id] = null;
    },
    getRequisitionDetailsFailure: (state, action) => {
      const { id, error } = action.payload;
      state.detailsLoading[id] = false;
      state.detailsError[id] = error;
    },

    // Approve / Reject Requisition
    approveRejectRequisitionStart: (state, action) => {
      state.actionLoading[action.payload] = true;
      state.error = null;
    },
    approveRejectRequisitionSuccess: (state, action) => {
      const { id, data } = action.payload;
      state.actionLoading[id] = false;
      state.error = null;

      state.requisitions = state.requisitions.map((req) =>
        String(req.id) === String(id)
          ? {
            ...req,
            status: data?.status || req.status,
            approved_at: data?.action_at || req.approved_at,
          }
          : req
      );

      state.myRequisitions = state.myRequisitions.map((req) =>
        String(req.id) === String(id)
          ? {
            ...req,
            status: data?.status || req.status,
            approved_at: data?.action_at || req.approved_at,
          }
          : req
      );

      if (state.requisitionDetails[id]) {
        state.requisitionDetails[id] = {
          ...state.requisitionDetails[id],
          status: data?.status || state.requisitionDetails[id].status,
          approval: data || state.requisitionDetails[id].approval,
        };
      }
    },
    approveRejectRequisitionFailure: (state, action) => {
      const { id, error } = action.payload;
      state.actionLoading[id] = false;
      state.error = error;
    },

    // Delete Requisition
    deleteRequisitionStart: (state, action) => {
      if (action.payload) {
        state.actionLoading[action.payload] = true;
      } else {
        state.loading = true;
      }
      state.error = null;
    },
    deleteRequisitionSuccess: (state, action) => {
      const id = action.payload;
      if (id) {
        state.actionLoading[id] = false;
        state.requisitions = state.requisitions.filter(
          (req) => String(req.id) !== String(id)
        );
        state.myRequisitions = state.myRequisitions.filter(
          (req) => String(req.id) !== String(id)
        );
        delete state.requisitionDetails[id];
      }
      state.loading = false;
      state.error = null;
    },
    deleteRequisitionFailure: (state, action) => {
      const { id, error } = action.payload || {};
      if (id) {
        state.actionLoading[id] = false;
      }
      state.loading = false;
      state.error = error || action.payload;
    },

    // Set Selected Requisition
    setSelectedRequisition: (state, action) => {
      state.selectedRequisition = action.payload;
    },

    // Clear Error
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  getRequisitionsStart,
  getRequisitionsSuccess,
  getRequisitionsFailure,
  getMyRequisitionsSuccess,
  createRequisitionStart,
  createRequisitionSuccess,
  createRequisitionFailure,
  updateRequisitionStart,
  updateRequisitionSuccess,
  updateRequisitionFailure,
  deleteRequisitionStart,
  deleteRequisitionSuccess,
  deleteRequisitionFailure,
  getRequisitionDetailsStart,
  getRequisitionDetailsSuccess,
  getRequisitionDetailsFailure,
  approveRejectRequisitionStart,
  approveRejectRequisitionSuccess,
  approveRejectRequisitionFailure,
  setSelectedRequisition,
  clearError,
} = requisitionSlice.actions;

export default requisitionSlice.reducer;


